import React, { memo, useMemo } from 'react';
import { LinearProgress } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { SvgSearch } from 'UI/res';

import { ResumeCard } from '../ResumeCard';

import { getCatalogColorById } from './ResumesList.constants';
import { useResumesListStyles } from './ResumesList.styled';
import { SelectionHeader } from './SelectionHeader';

const ResumesListComponent = memo(
  ({
    areSomeSelected,
    isLoading,
    isSelected,
    onSelectionChange,
    onToggleSelection,
    onViewDetails,
    pagination,
    selectionAction,
    resumes = [],
    candidateTypeColors = {},
    candidateStatusColors = {},
    scrollRef,
    selectedIdsOnPage,
    totalResults = 0,
    totalSelected
  }) => {
    const classes = useResumesListStyles();

    const allOnPageSelected = useMemo(() => {
      if (!selectedIdsOnPage || !resumes.length) return false;
      const pageIds = resumes.map(r => r.candidate?.id).filter(Boolean);
      if (pageIds.length === 0) return false;
      const selectedSet = new Set(selectedIdsOnPage);
      return pageIds.every(id => selectedSet.has(id));
    }, [selectedIdsOnPage, resumes]);

    if (resumes.length === 0 && !isLoading) {
      return (
        <EmptyPlaceholder
          title={strings.resumeSearch.resumesList.emptyState.title}
          subtitle={strings.resumeSearch.resumesList.emptyState.subtitle}
          customEmptyState={<SvgSearch height={400} />}
        />
      );
    }

    return (
      <Box className={classes.listContainer}>
        {!isLoading && (
          <Box display="flex" flexDirection="column" mr={2} borderBottom={1} borderColor="divider">
            <Box display="flex" alignItems="center" width="100%">
              <SelectionHeader
                action={selectionAction}
                allOnPageSelected={allOnPageSelected}
                areSomeSelected={areSomeSelected}
                itemsOnPage={resumes.length}
                onSelectionChange={onSelectionChange}
                totalResults={totalResults}
                totalSelected={totalSelected}
              />
            </Box>
          </Box>
        )}
        <Box className={classes.scrollableContent} ref={scrollRef}>
          <When condition={isLoading}>
            <Box width="100%" pr={2} mt={2}>
              <LinearProgress />
            </Box>
          </When>
          <When condition={!isLoading}>
            <Box component="ul" m={0} p={0}>
              {resumes.map((resume, index) => (
                <ResumeCard
                  key={resume.candidate?.id ?? resume.meta?.sovren_document_id ?? `resume-${index}`}
                  resume={resume}
                  candidateTypeColor={getCatalogColorById(
                    candidateTypeColors,
                    resume?.candidate?.type?.id
                  )}
                  candidateStatusColor={getCatalogColorById(
                    candidateStatusColors,
                    resume?.candidate?.status?.id
                  )}
                  isSelected={isSelected ? isSelected(resume.candidate?.id) : false}
                  onSelectionChange={onToggleSelection}
                  onViewDetails={onViewDetails}
                />
              ))}
            </Box>
          </When>
        </Box>

        {pagination}
      </Box>
    );
  }
);

export { ResumesListComponent as ResumesList };
